import { useState, useEffect } from 'react'
import { useChatCardsCopy } from './chat-cards-copy'
import {
  onBlockDiff,
  type BlockDiffData,
} from '../services/generation-state'

// ─── Hook ─────────────────────────────────────────────────────

export function useBlockDiff(): BlockDiffData | null {
  const [diff, setDiff] = useState<BlockDiffData | null>(null)

  useEffect(() => {
    return onBlockDiff(setDiff)
  }, [])

  return diff
}

// ─── Props ────────────────────────────────────────────────────

export interface BlockDiffCardProps {
  blockName: string
  beforeSummary: string
  afterSummary: string
  changedFields: string[]
  onApply: () => void
  onUndo: () => void
  applied: boolean
}

// ─── Component ────────────────────────────────────────────────

export function BlockDiffCard({
  blockName,
  beforeSummary,
  afterSummary,
  changedFields,
  onApply,
  onUndo,
  applied,
}: BlockDiffCardProps) {
  const copy = useChatCardsCopy().blockDiff

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      {/* Header: block name + modification icon */}
      <div className="flex items-center gap-2">
        <svg
          className="w-4 h-4 text-muted-foreground shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
        <span className="text-sm font-medium truncate">{blockName}</span>
        {applied && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
            {copy.applied}
          </span>
        )}
      </div>

      {/* Side-by-side Before/After */}
      <div className="grid grid-cols-2 gap-3">
        {/* Before */}
        <div className="border-l-2 border-border bg-muted/20 p-3 rounded">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {copy.before}
          </span>
          <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
            {beforeSummary}
          </p>
        </div>

        {/* After */}
        <div className="border-l-2 border-foreground/30 bg-muted/20 p-3 rounded">
          <span className="text-xs font-medium text-foreground uppercase tracking-wide">
            {copy.after}
          </span>
          <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
            {afterSummary}
          </p>
        </div>
      </div>

      {/* Changed fields */}
      {changedFields.length > 0 && (
        <div className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">
            {copy.changedFields}
          </span>
          <div className="flex flex-wrap gap-1">
            {changedFields.map((field) => (
              <span
                key={field}
                className="inline-flex items-center px-1.5 py-0.5 rounded border border-border bg-muted text-xs text-muted-foreground"
              >
                {field}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={onApply}
          disabled={applied}
          className="border border-border bg-foreground text-background px-4 py-1.5 rounded text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-foreground/90 transition-colors"
        >
          {copy.apply}
        </button>
        {applied && (
          <button
            onClick={onUndo}
            className="border border-input px-4 py-1.5 rounded text-xs font-medium hover:bg-accent transition-colors"
          >
            {copy.undo}
          </button>
        )}
      </div>
    </div>
  )
}
